import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import useAuth from "../../hooks/useAuth";
import useIsMobileList from "../../hooks/useIsMobileList";
import { APP_HOME_PATH } from "../../config/routes";
import { getTenantsList } from "../../api/tenant";
import {
  listMedicineProducts,
  createMedicineProduct,
  updateMedicineProduct,
  listMedicineLots,
  createMedicineLot,
  updateMedicineLot,
} from "../../api/medicine";
import { getTreatments } from "../../api/treatment";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";
import { useNotify } from "../../utils/useNotify";

const CHART_COLORS = [
  "#2e7d32",
  "#1976d2",
  "#ed6c02",
  "#9c27b0",
  "#00838f",
  "#6d4c41",
  "#c62828",
  "#5e35b1",
];

/** Consistent badge colours per ledger column */
const LEDGER_CHIP = {
  qty: { bgcolor: "#e3f2fd", color: "#1565c0", borderColor: "#90caf9" },
  cost: { bgcolor: "#e8f5e9", color: "#2e7d32", borderColor: "#a5d6a7" },
  scope: { bgcolor: "#f3e5f5", color: "#7b1fa2", borderColor: "#ce93d8" },
  subject: { bgcolor: "#fff3e0", color: "#e65100", borderColor: "#ffcc80" },
};

function chartBarHeight(itemCount, rowHeight = 34, min = 160, max = 380) {
  if (!itemCount) return min;
  return Math.min(max, Math.max(min, itemCount * rowHeight + 56));
}

function LedgerChip({ label, column }) {
  const style = LEDGER_CHIP[column];
  return (
    <Chip
      size="small"
      label={label}
      component="span"
      clickable={false}
      sx={{
        height: 24,
        fontSize: "0.7rem",
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: "1px solid",
        borderColor: style.borderColor,
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function DashboardChartCard({ title, caption, children }) {
  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: caption ? 0.5 : 1, fontWeight: 700 }}>
        {title}
      </Typography>
      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, lineHeight: 1.35 }}
        >
          {caption}
        </Typography>
      ) : null}
      <Box sx={{ width: "100%", minWidth: 0 }}>{children}</Box>
    </Paper>
  );
}

function HorizontalRankBarChart({
  data,
  valueKey,
  nameKey = "name",
  height,
  colors = CHART_COLORS,
  formatValue = formatGroupedQty,
  formatTooltip,
  tooltipLabel = "Quantity",
  yAxisWidth = 88,
}) {
  const tooltipFormat = formatTooltip || formatValue;
  if (!data?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
        No data yet
      </Typography>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={formatValue} tick={{ fontSize: 10 }} />
        <YAxis
          type="category"
          dataKey={nameKey}
          width={yAxisWidth}
          tick={{ fontSize: 10 }}
          interval={0}
        />
        <ReTooltip formatter={(value) => [tooltipFormat(value), tooltipLabel]} />
        <Bar dataKey={valueKey} radius={[0, 4, 4, 0]} barSize={16}>
          {data.map((entry, idx) => (
            <Cell key={entry[nameKey] || idx} fill={colors[idx % colors.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function UsageLedgerCard({ row }) {
  const subject =
    row.treatment_scope === "group"
      ? row.group_name || "Group"
      : row.animal_tag_number || row.animal_id || "Animal";
  const cost =
    row.dispensed_total_cost == null
      ? "—"
      : formatMoney(row.dispensed_total_cost, 2);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main" noWrap sx={{ minWidth: 0 }}>
          {row.medicine || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          {row.date || "—"}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.5}
        sx={{ mt: 0.75, rowGap: 0.5 }}
      >
        <LedgerChip
          label={`${row.dispensed_qty ?? "—"} ${row.dispensed_unit || ""}`.trim()}
          column="qty"
        />
        <LedgerChip label={`Cost ${cost}`} column="cost" />
        <LedgerChip label={row.treatment_scope || "—"} column="scope" />
        <LedgerChip label={subject} column="subject" />
      </Stack>
    </Paper>
  );
}

function MedicineDashStatCard({ label, value, helper, accent, valueColor }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.25,
        height: "100%",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "0 auto 0 0",
          width: 3,
          bgcolor: accent || "primary.main",
        },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          fontWeight: 600,
          letterSpacing: 0.35,
          lineHeight: 1.25,
          textTransform: "uppercase",
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          mt: 0.25,
          lineHeight: 1.2,
          color: valueColor || "text.primary",
          fontSize: { xs: "1rem", sm: "1.125rem" },
        }}
      >
        {value}
      </Typography>
      {helper ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25, lineHeight: 1.25 }}
        >
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

const PRODUCT_STATUS = {
  active: {
    borderColor: "#a5d6a7",
    bgcolor: "#f1f8e9",
    accent: "#2e7d32",
    chipBg: "#2e7d32",
    label: "Active",
  },
  inactive: {
    borderColor: "#e0e0e0",
    bgcolor: "#fafafa",
    accent: "#bdbdbd",
    chipBg: "#757575",
    label: "Inactive",
  },
};

function ProductCard({ product, onToggle, toggling }) {
  const isActive = Boolean(product.is_active);
  const style = isActive ? PRODUCT_STATUS.active : PRODUCT_STATUS.inactive;

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: style.borderColor,
        bgcolor: style.bgcolor,
        borderLeft: `4px solid ${style.accent}`,
        opacity: isActive ? 1 : 0.88,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color={isActive ? "text.primary" : "text.secondary"}
            noWrap
          >
            {product.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
          >
            {product.brand || "No brand"}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={style.label}
          component="span"
          clickable={false}
          sx={{
            height: 22,
            fontWeight: 700,
            fontSize: "0.7rem",
            flexShrink: 0,
            bgcolor: style.chipBg,
            color: "#fff",
            pointerEvents: "none",
            cursor: "default",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1 }}
      >
        <LedgerChip label={product.default_unit || "—"} column="qty" />
        <Button
          size="small"
          variant={isActive ? "outlined" : "contained"}
          color={isActive ? "warning" : "success"}
          disabled={toggling}
          onClick={() => onToggle(product)}
          sx={{ minWidth: 100, fontWeight: 600 }}
        >
          {isActive ? "Deactivate" : "Activate"}
        </Button>
      </Stack>
    </Paper>
  );
}

function MobilePagination({ page, pageSize, rowCount, loading, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(rowCount / pageSize));
  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, pt: 1 }}>
      <Button
        size="small"
        disabled={page === 0 || loading}
        onClick={() => onPageChange({ page: page - 1, pageSize })}
      >
        Previous
      </Button>
      <Typography variant="body2" sx={{ alignSelf: "center" }}>
        Page {page + 1} of {totalPages}
      </Typography>
      <Button
        size="small"
        disabled={loading || (page + 1) * pageSize >= rowCount}
        onClick={() => onPageChange({ page: page + 1, pageSize })}
      >
        Next
      </Button>
    </Box>
  );
}

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const formatMoney = (value, fractionDigits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

/** Quantities on axes and pie labels: grouped thousands, up to 2 decimals when needed. */
const formatGroupedQty = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(num);
};

const LOT_STATUS = {
  inStock: {
    label: "In stock",
    borderColor: "#a5d6a7",
    bgcolor: "#f1f8e9",
    accent: "#2e7d32",
    chipBg: "#2e7d32",
  },
  expiring: {
    label: "Expiring soon",
    borderColor: "#ffcc80",
    bgcolor: "#fff8e1",
    accent: "#ed6c02",
    chipBg: "#ed6c02",
  },
  expired: {
    label: "Expired",
    borderColor: "#ef9a9a",
    bgcolor: "#ffebee",
    accent: "#c62828",
    chipBg: "#c62828",
  },
  depleted: {
    label: "Depleted",
    borderColor: "#e0e0e0",
    bgcolor: "#fafafa",
    accent: "#757575",
    chipBg: "#757575",
  },
};

function parseDateOnly(value) {
  if (!value) return null;
  const parts = String(value).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function daysUntilDate(dateOnly) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((dateOnly - today) / 86400000);
}

function getLotStatus(row) {
  const qty = Number(row.quantity_remaining);
  if (Number.isFinite(qty) && qty <= 0) return "depleted";

  const expiry = parseDateOnly(row.expiry_date);
  if (expiry) {
    const days = daysUntilDate(expiry);
    if (days < 0) return "expired";
    if (days <= 30) return "expiring";
  }
  return "inStock";
}

function LotStatusChip({ statusKey }) {
  const style = LOT_STATUS[statusKey];
  return (
    <Chip
      size="small"
      label={style.label}
      component="span"
      clickable={false}
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: "0.7rem",
        flexShrink: 0,
        bgcolor: style.chipBg,
        color: "#fff",
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function lotLineValue(row) {
  const qty = Number(row.quantity_remaining);
  const unitCost = Number(row.unit_cost);
  if (!Number.isFinite(qty) || !Number.isFinite(unitCost)) return "—";
  return formatMoney(qty * unitCost, 2);
}

function LotCard({ row, productName, onEdit }) {
  const statusKey = getLotStatus(row);
  const style = LOT_STATUS[statusKey];
  const expiryLabel = row.expiry_date ? String(row.expiry_date).slice(0, 10) : "—";
  const qtyLabel = `${row.quantity_remaining ?? "—"} ${row.unit || ""}`.trim();
  const unitCostLabel =
    row.unit_cost == null ? "—" : formatMoney(row.unit_cost, 4);
  const batchLabel = row.batch_number ? `Batch ${row.batch_number}` : `Lot #${row.id}`;
  const metaLabel = row.supplier ? `${batchLabel} · ${row.supplier}` : batchLabel;

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: style.borderColor,
        bgcolor: style.bgcolor,
        borderLeft: `4px solid ${style.accent}`,
        opacity: statusKey === "depleted" ? 0.9 : 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="primary.main"
            noWrap
          >
            {productName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
          >
            {metaLabel}
          </Typography>
        </Box>
        <LotStatusChip statusKey={statusKey} />
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.5}
        sx={{ mt: 0.75, rowGap: 0.5 }}
      >
        <LedgerChip label={qtyLabel} column="qty" />
        <LedgerChip label={`Cost ${unitCostLabel}`} column="cost" />
        <LedgerChip label={`Value ${lotLineValue(row)}`} column="scope" />
        <LedgerChip label={`Exp ${expiryLabel}`} column="subject" />
      </Stack>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onEdit(row)}
          sx={{ fontWeight: 600 }}
        >
          Edit
        </Button>
      </Stack>
    </Paper>
  );
}

export default function MedicineCabinet() {
  const { user } = useAuth();
  const notify = useNotify();
  const isMobile = useIsMobileList("md");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(0);
  const LEDGER_PAGE_SIZE = 10;

  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);

  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  const effectiveTenantId = isSuperAdmin
    ? tenantFilter?.id ?? null
    : user?.tenant_id ?? null;

  const farmTenantParam =
    isSuperAdmin && effectiveTenantId != null ? effectiveTenantId : null;

  useEffect(() => {
    if (!isSuperAdmin) return;
    let c = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!c) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!c) setTenants([]);
      }
    })();
    return () => {
      c = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || tenants.length === 0 || tenantFilter != null) return;
    const raw = searchParams.get("tenant_id");
    if (!raw) return;
    const id = Number(raw);
    const t = tenants.find((x) => x.id === id);
    if (t) setTenantFilter(t);
  }, [isSuperAdmin, tenants, searchParams, tenantFilter]);

  const onOrgChange = (_, v) => {
    setTenantFilter(v);
    if (isSuperAdmin && v?.id != null) {
      setSearchParams({ tenant_id: String(v.id) }, { replace: true });
    }
  };

  const apiTenant = isSuperAdmin ? farmTenantParam : null;
  const DASH_PAGE_SIZE = 100;

  // --- Products grid ---
  const [prodRows, setProdRows] = useState([]);
  const [prodCount, setProdCount] = useState(0);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodPage, setProdPage] = useState({ page: 0, pageSize: 10 });
  const [togglingId, setTogglingId] = useState(null);

  const fetchProducts = useCallback(async () => {
    if (isSuperAdmin && farmTenantParam == null) {
      setProdRows([]);
      setProdCount(0);
      return;
    }
    if (!isSuperAdmin && user?.tenant_id == null) return;
    setProdLoading(true);
    try {
      const data = await listMedicineProducts(
        {
          page: prodPage.page + 1,
          pageSize: prodPage.pageSize,
        },
        apiTenant
      );
      setProdRows(data.items || []);
      setProdCount(data.total ?? 0);
    } catch {
      setProdRows([]);
      setProdCount(0);
    } finally {
      setProdLoading(false);
    }
  }, [isSuperAdmin, farmTenantParam, user?.tenant_id, prodPage, apiTenant]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 200);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  const handleToggleProduct = useCallback(
    async (product) => {
      const nextActive = !product.is_active;
      setTogglingId(product.id);
      try {
        await updateMedicineProduct(
          product.id,
          { is_active: nextActive },
          apiTenant
        );
        await fetchProducts();
        notify.success(
          nextActive
            ? `"${product.name}" is now active in the formulary.`
            : `"${product.name}" has been deactivated.`
        );
      } catch {
        notify.error("Could not update product status. Please try again.");
      } finally {
        setTogglingId(null);
      }
    },
    [apiTenant, fetchProducts, notify]
  );

  // --- Lots grid ---
  const [lotRows, setLotRows] = useState([]);
  const [lotCount, setLotCount] = useState(0);
  const [lotLoading, setLotLoading] = useState(false);
  const [lotPage, setLotPage] = useState({ page: 0, pageSize: 10 });

  const fetchLots = useCallback(async () => {
    if (isSuperAdmin && farmTenantParam == null) {
      setLotRows([]);
      setLotCount(0);
      return;
    }
    if (!isSuperAdmin && user?.tenant_id == null) return;
    setLotLoading(true);
    try {
      const data = await listMedicineLots(
        {
          page: lotPage.page + 1,
          pageSize: lotPage.pageSize,
        },
        apiTenant
      );
      setLotRows(data.items || []);
      setLotCount(data.total ?? 0);
    } catch {
      setLotRows([]);
      setLotCount(0);
    } finally {
      setLotLoading(false);
    }
  }, [isSuperAdmin, farmTenantParam, user?.tenant_id, lotPage, apiTenant]);

  useEffect(() => {
    const t = setTimeout(fetchLots, 200);
    return () => clearTimeout(t);
  }, [fetchLots]);

  // --- Stock dashboard data ---
  const [dashLoading, setDashLoading] = useState(false);
  const [dashProducts, setDashProducts] = useState([]);
  const [dashLots, setDashLots] = useState([]);
  const [usageRows, setUsageRows] = useState([]);

  const fetchAllPages = useCallback(async (fetcher) => {
    let page = 1;
    let pages = 1;
    const out = [];
    while (page <= pages) {
      const res = await fetcher(page, DASH_PAGE_SIZE);
      const items = Array.isArray(res?.items) ? res.items : [];
      out.push(...items);
      pages = Number(res?.pages || 1);
      page += 1;
      if (page > 200) break; // hard safety cap
    }
    return out;
  }, []);

  const loadStockDashboard = useCallback(async () => {
    if (isSuperAdmin && farmTenantParam == null) {
      setDashProducts([]);
      setDashLots([]);
      setUsageRows([]);
      return;
    }
    if (!isSuperAdmin && user?.tenant_id == null) return;
    setDashLoading(true);
    try {
      const [p, l, u] = await Promise.all([
        fetchAllPages((page, pageSize) =>
          listMedicineProducts({ page, pageSize }, apiTenant)
        ),
        fetchAllPages((page, pageSize) =>
          listMedicineLots({ page, pageSize }, apiTenant)
        ),
        fetchAllPages((page, pageSize) =>
          getTreatments({ page, pageSize, sortBy: "date", sortDir: "desc" }, apiTenant)
        ),
      ]);
      setDashProducts(p);
      setDashLots(l);
      setUsageRows(u);
    } catch {
      setDashProducts([]);
      setDashLots([]);
      setUsageRows([]);
    } finally {
      setDashLoading(false);
    }
  }, [isSuperAdmin, farmTenantParam, user?.tenant_id, apiTenant, fetchAllPages]);

  useEffect(() => {
    const t = setTimeout(loadStockDashboard, 250);
    return () => clearTimeout(t);
  }, [loadStockDashboard]);

  // --- Add product dialog ---
  const [addProdOpen, setAddProdOpen] = useState(false);
  const [newProd, setNewProd] = useState({
    name: "",
    brand: "",
    default_unit: "ml",
  });

  const submitNewProduct = async () => {
    if (!newProd.name?.trim()) return;
    try {
      await createMedicineProduct(
        {
          name: newProd.name.trim(),
          brand: newProd.brand?.trim() || null,
          default_unit: newProd.default_unit?.trim() || "ml",
        },
        apiTenant
      );
      setAddProdOpen(false);
      setNewProd({ name: "", brand: "", default_unit: "ml" });
      fetchProducts();
    } catch {
      /* snack optional */
    }
  };

  // --- Receive lot dialog ---
  const [lotOpen, setLotOpen] = useState(false);
  const [prodOptions, setProdOptions] = useState([]);
  const [lotForm, setLotForm] = useState({
    product: null,
    quantity_remaining: "",
    unit: "ml",
    unit_cost: "",
    currency: "UGX",
    batch_number: "",
    expiry_date: "",
    supplier: "",
  });
  const [editLotOpen, setEditLotOpen] = useState(false);
  const [editingLotId, setEditingLotId] = useState(null);
  const [editLotForm, setEditLotForm] = useState({
    quantity_remaining: "",
    unit: "ml",
    unit_cost: "",
    currency: "UGX",
    batch_number: "",
    expiry_date: "",
    supplier: "",
  });

  const loadProdOptions = async () => {
    try {
      const data = await listMedicineProducts(
        { page: 1, pageSize: 100, activeOnly: true },
        apiTenant
      );
      setProdOptions(data.items || []);
    } catch {
      setProdOptions([]);
    }
  };

  const openLotDialog = () => {
    loadProdOptions();
    setLotOpen(true);
  };

  const submitLot = async () => {
    const pid = lotForm.product?.id;
    const qty = String(lotForm.quantity_remaining || "").trim();
    if (!pid || !qty) return;
    try {
      await createMedicineLot(
        {
          product_id: pid,
          quantity_remaining: qty,
          unit: lotForm.unit?.trim() || "ml",
          unit_cost:
            String(lotForm.unit_cost ?? "").trim() !== ""
              ? String(lotForm.unit_cost).trim()
              : null,
          currency: lotForm.currency?.trim() || "UGX",
          batch_number: lotForm.batch_number?.trim() || null,
          expiry_date: lotForm.expiry_date?.trim() || null,
          supplier: lotForm.supplier?.trim() || null,
        },
        apiTenant
      );
      setLotOpen(false);
      setLotForm({
        product: null,
        quantity_remaining: "",
        unit: "ml",
        unit_cost: "",
        currency: "UGX",
        batch_number: "",
        expiry_date: "",
        supplier: "",
      });
      fetchLots();
    } catch {
      /* surface via alert if needed */
    }
  };

  const openEditLotDialog = (row) => {
    setEditingLotId(row.id);
    setEditLotForm({
      quantity_remaining:
        row.quantity_remaining == null ? "" : String(row.quantity_remaining),
      unit: row.unit || "ml",
      unit_cost: row.unit_cost == null ? "" : String(row.unit_cost),
      currency: row.currency || "UGX",
      batch_number: row.batch_number || "",
      expiry_date: row.expiry_date ? String(row.expiry_date).slice(0, 10) : "",
      supplier: row.supplier || "",
    });
    setEditLotOpen(true);
  };

  const submitEditLot = async () => {
    const qty = String(editLotForm.quantity_remaining || "").trim();
    if (!editingLotId || !qty) return;
    try {
      await updateMedicineLot(
        editingLotId,
        {
          quantity_remaining: qty,
          unit: editLotForm.unit?.trim() || "ml",
          unit_cost:
            String(editLotForm.unit_cost ?? "").trim() !== ""
              ? String(editLotForm.unit_cost).trim()
              : null,
          currency: editLotForm.currency?.trim() || "UGX",
          batch_number: editLotForm.batch_number?.trim() || null,
          expiry_date: editLotForm.expiry_date?.trim() || null,
          supplier: editLotForm.supplier?.trim() || null,
        },
        apiTenant
      );
      setEditLotOpen(false);
      setEditingLotId(null);
      fetchLots();
      loadStockDashboard();
    } catch {
      /* surface via alert if needed */
    }
  };

  const prodColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 72 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "brand", headerName: "Brand", width: 120 },
      { field: "default_unit", headerName: "Unit", width: 80 },
      {
        field: "is_active",
        headerName: "Status",
        width: 110,
        renderCell: (p) => {
          const active = Boolean(p.value);
          const style = active ? PRODUCT_STATUS.active : PRODUCT_STATUS.inactive;
          return (
            <Chip
              size="small"
              label={style.label}
              component="span"
              clickable={false}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.7rem",
                bgcolor: style.chipBg,
                color: "#fff",
                pointerEvents: "none",
                cursor: "default",
              }}
            />
          );
        },
      },
      {
        field: "toggle",
        headerName: "",
        sortable: false,
        width: 120,
        renderCell: (p) => (
          <Button
            size="small"
            variant={p.row.is_active ? "outlined" : "contained"}
            color={p.row.is_active ? "warning" : "success"}
            disabled={togglingId === p.row.id}
            onClick={() => handleToggleProduct(p.row)}
          >
            {p.row.is_active ? "Deactivate" : "Activate"}
          </Button>
        ),
      },
    ],
    [handleToggleProduct, togglingId]
  );

  const lotProductNameMap = useMemo(() => {
    const m = new Map();
    dashProducts.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [dashProducts]);

  const lotColumns = useMemo(
    () => [
      { field: "id", headerName: "Lot", width: 72 },
      { field: "product_id", headerName: "Product ID", width: 100 },
      {
        field: "product_name",
        headerName: "Product",
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) =>
          lotProductNameMap.get(row.product_id) || `Product ${row.product_id}`,
      },
      { field: "batch_number", headerName: "Batch", flex: 1, minWidth: 100 },
      {
        field: "lot_status",
        headerName: "Status",
        width: 130,
        sortable: false,
        valueGetter: (_, row) => getLotStatus(row),
        renderCell: (p) => <LotStatusChip statusKey={p.value} />,
      },
      {
        field: "expiry_date",
        headerName: "Expiry",
        width: 120,
        valueGetter: (v, row) =>
          row.expiry_date ? String(row.expiry_date).slice(0, 10) : "—",
      },
      {
        field: "quantity_remaining",
        headerName: "Qty left",
        width: 100,
      },
      { field: "unit", headerName: "Unit", width: 72 },
      {
        field: "unit_cost",
        headerName: "Unit cost",
        width: 120,
        valueGetter: (v, row) =>
          row.unit_cost == null ? "—" : formatMoney(row.unit_cost, 4),
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 100,
        valueGetter: (v, row) => row.currency || "UGX",
      },
      {
        field: "line_value",
        headerName: "Stock value",
        width: 130,
        valueGetter: (v, row) => {
          const qty = Number(row.quantity_remaining);
          const unitCost = Number(row.unit_cost);
          if (!Number.isFinite(qty) || !Number.isFinite(unitCost)) return "—";
          return formatMoney(qty * unitCost, 2);
        },
      },
      { field: "supplier", headerName: "Supplier", flex: 1, minWidth: 120 },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        width: 100,
        renderCell: (p) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openEditLotDialog(p.row)}
            sx={{ fontWeight: 600 }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [lotProductNameMap, openEditLotDialog]
  );

  const productMap = useMemo(() => {
    const m = new Map();
    dashProducts.forEach((p) => m.set(p.id, p));
    return m;
  }, [dashProducts]);

  const stockByProduct = useMemo(() => {
    const agg = new Map();
    dashLots.forEach((lot) => {
      const key = lot.product_id;
      const q = Number(lot.quantity_remaining || 0);
      const prev = agg.get(key) || 0;
      agg.set(key, prev + (Number.isFinite(q) ? q : 0));
    });
    return Array.from(agg.entries())
      .map(([productId, qty]) => ({
        productId,
        name: productMap.get(productId)?.name || `Product ${productId}`,
        qty: Number(qty.toFixed(2)),
      }))
      .sort((a, b) => b.qty - a.qty);
  }, [dashLots, productMap]);

  /** Matches Stock lots grid "Stock value": sum of qty remaining × unit cost per lot. */
  const totalStockValueAtCost = useMemo(
    () =>
      dashLots.reduce((sum, lot) => {
        const qty = Number(lot.quantity_remaining);
        const unitCost = Number(lot.unit_cost);
        if (!Number.isFinite(qty) || !Number.isFinite(unitCost)) return sum;
        return sum + qty * unitCost;
      }, 0),
    [dashLots]
  );

  const stockValueByProduct = useMemo(() => {
    const m = new Map();
    dashLots.forEach((lot) => {
      const name = productMap.get(lot.product_id)?.name || `Product ${lot.product_id}`;
      const qty = Number(lot.quantity_remaining);
      const unitCost = Number(lot.unit_cost);
      if (!Number.isFinite(qty) || !Number.isFinite(unitCost)) return;
      m.set(name, (m.get(name) || 0) + qty * unitCost);
    });
    return Array.from(m.entries())
      .map(([name, total]) => ({ name, total: Number(total.toFixed(2)) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [dashLots, productMap]);

  const lowStockProducts = useMemo(
    () =>
      dashProducts.filter((p) => {
        const total = stockByProduct.find((x) => x.productId === p.id)?.qty ?? 0;
        const reorder = Number(p.reorder_level ?? 0);
        return reorder > 0 && total <= reorder;
      }),
    [dashProducts, stockByProduct]
  );

  const usageByMedicine = useMemo(() => {
    const m = new Map();
    usageRows.forEach((u) => {
      const key = u.medicine || "Unknown";
      const q = Number(u.dispensed_qty);
      // Fallback to 1 treatment event when qty isn't captured.
      const add = Number.isFinite(q) && q > 0 ? q : 1;
      m.set(key, (m.get(key) || 0) + add);
    });
    return Array.from(m.entries())
      .map(([name, qty]) => ({ name, qty: Number(qty.toFixed(2)) }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 8);
  }, [usageRows]);

  const lotUsageRows = useMemo(
    () =>
      usageRows.filter(
        (t) => t.medicine_lot_id != null || t.dispensed_qty != null
      ),
    [usageRows]
  );

  const stockConfigured = dashProducts.length > 0 || dashLots.length > 0;
  const hasTreatmentUsage = usageRows.length > 0;
  const noStockButHasUsage = !stockConfigured && hasTreatmentUsage;

  const usageByScope = useMemo(() => {
    const m = new Map();
    usageRows.forEach((u) => {
      const key = u.treatment_scope || "individual";
      const q = Number(u.dispensed_qty);
      const add = Number.isFinite(q) && q > 0 ? q : 1;
      m.set(key, (m.get(key) || 0) + add);
    });
    return Array.from(m.entries())
      .map(([name, qty]) => ({ name, qty: Number(qty.toFixed(2)) }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
  }, [usageRows]);

  const costedUsageRows = useMemo(
    () =>
      usageRows.filter((u) => {
        const v = Number(u.dispensed_total_cost);
        return Number.isFinite(v) && v > 0;
      }),
    [usageRows]
  );

  const totalSpend = useMemo(
    () =>
      costedUsageRows.reduce(
        (sum, u) => sum + (Number(u.dispensed_total_cost) || 0),
        0
      ),
    [costedUsageRows]
  );

  const avgCostPerTreatment = useMemo(
    () => (costedUsageRows.length ? totalSpend / costedUsageRows.length : 0),
    [costedUsageRows.length, totalSpend]
  );

  const groupTreatmentSpend = useMemo(
    () =>
      costedUsageRows.reduce((sum, u) => {
        if (u.treatment_scope === "group") {
          return sum + (Number(u.dispensed_total_cost) || 0);
        }
        return sum;
      }, 0),
    [costedUsageRows]
  );

  const uncostedUsageCount = useMemo(
    () => usageRows.length - costedUsageRows.length,
    [usageRows.length, costedUsageRows.length]
  );

  const spendByMedicine = useMemo(() => {
    const m = new Map();
    costedUsageRows.forEach((u) => {
      const key = u.medicine || "Unknown";
      m.set(key, (m.get(key) || 0) + (Number(u.dispensed_total_cost) || 0));
    });
    return Array.from(m.entries())
      .map(([name, total]) => ({ name, total: Number(total.toFixed(2)) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [costedUsageRows]);

  const spendByMedicineChart = useMemo(
    () => (spendByMedicine.length > 0 ? spendByMedicine : stockValueByProduct),
    [spendByMedicine, stockValueByProduct]
  );

  const spendTrend = useMemo(() => {
    const m = new Map();
    costedUsageRows.forEach((u) => {
      const d = String(u.date || "").slice(0, 10);
      if (!d) return;
      m.set(d, (m.get(d) || 0) + (Number(u.dispensed_total_cost) || 0));
    });
    return Array.from(m.entries())
      .map(([date, total]) => ({ date, total: Number(total.toFixed(2)) }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [costedUsageRows]);

  const dashboardCurrency = useMemo(
    () =>
      costedUsageRows.find((u) => u.dispensed_currency)?.dispensed_currency ||
      dashLots.find((l) => l.currency)?.currency ||
      "UGX",
    [costedUsageRows, dashLots]
  );

  const ledgerRows = useMemo(() => lotUsageRows.slice(0, 100), [lotUsageRows]);

  const ledgerPagedRows = useMemo(
    () =>
      ledgerRows.slice(
        ledgerPage * LEDGER_PAGE_SIZE,
        ledgerPage * LEDGER_PAGE_SIZE + LEDGER_PAGE_SIZE
      ),
    [ledgerRows, ledgerPage]
  );

  useEffect(() => {
    setLedgerPage(0);
  }, [tab, ledgerRows.length]);

  const dashStatCards = useMemo(
    () => [
      {
        label: "Products tracked",
        value: dashProducts.length,
        accent: "#1565c0",
      },
      {
        label: "Stock lots",
        value: dashLots.length,
        accent: "#2e7d32",
      },
      {
        label: "Low stock",
        value: lowStockProducts.length,
        accent: lowStockProducts.length ? "#ed6c02" : "#2e7d32",
        valueColor: lowStockProducts.length ? "warning.main" : "success.main",
      },
      {
        label: "Usage records",
        value: usageRows.length,
        accent: "#6a1b9a",
      },
      {
        label: "Stock value",
        value: `${dashboardCurrency} ${formatMoney(totalStockValueAtCost, 2)}`,
        helper:
          totalSpend > 0
            ? `Usage cost: ${dashboardCurrency} ${formatMoney(totalSpend, 2)}`
            : null,
        accent: "#00838f",
      },
      {
        label: "Avg / treatment",
        value: `${dashboardCurrency} ${formatMoney(avgCostPerTreatment, 2)}`,
        accent: "#5e35b1",
      },
      {
        label: "Group spend",
        value: `${dashboardCurrency} ${formatMoney(groupTreatmentSpend, 2)}`,
        accent: "#c62828",
      },
      {
        label: "Uncosted",
        value: uncostedUsageCount,
        accent: uncostedUsageCount > 0 ? "#ed6c02" : "#2e7d32",
        valueColor: uncostedUsageCount > 0 ? "warning.main" : "success.main",
      },
    ],
    [
      dashProducts.length,
      dashLots.length,
      lowStockProducts.length,
      usageRows.length,
      dashboardCurrency,
      totalStockValueAtCost,
      totalSpend,
      avgCostPerTreatment,
      groupTreatmentSpend,
      uncostedUsageCount,
    ]
  );

  const stockChartHeight = chartBarHeight(stockByProduct.length);
  const usageMedChartHeight = chartBarHeight(usageByMedicine.length);
  const usageScopeChartHeight = chartBarHeight(usageByScope.length, 40, 140, 220);
  const spendChartHeight = chartBarHeight(spendByMedicineChart.length);
  const chartYAxisWidth = isMobile ? 76 : 100;
  const spendTooltipLabel =
    spendByMedicine.length > 0 ? "Spend" : "Stock value";
  const formatSpendAxis = (v) => formatMoney(Number(v), 0);
  const formatSpendTooltip = (value) =>
    `${dashboardCurrency} ${formatMoney(Number(value), 2)}`;

  const lotLineTotalPreview = useMemo(() => {
    const qty = Number(lotForm.quantity_remaining);
    const cost = Number(lotForm.unit_cost);
    if (!Number.isFinite(qty) || !Number.isFinite(cost) || qty <= 0 || cost < 0) {
      return null;
    }
    return (qty * cost).toFixed(2);
  }, [lotForm.quantity_remaining, lotForm.unit_cost]);

  if (isSuperAdmin && tenantFilter == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Medicines</Typography>
        </Breadcrumbs>
        <Typography variant="h4" gutterBottom>
          Medicine cabinet
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to manage formulary and stock.
        </Alert>
        <Box sx={{ maxWidth: 480 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField {...params} label="Organization" />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      </Box>
    );
  }

  if (!isSuperAdmin && user?.tenant_id == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Medicines are available once you belong to an organization.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%", width: "100%" }}>
      <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Typography color="text.primary">Medicines</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
        Medicine cabinet
      </Typography>

      {isSuperAdmin && (
        <Box sx={{ maxWidth: 480, my: 2 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField {...params} label="Organization" />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      )}

      <Paper sx={{ px: { xs: 1, sm: 2 }, pt: 1, overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="Stock dashboard" />
          <Tab label="Formulary" />
          <Tab label="Stock lots" />
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        <Box
          sx={{
            mb: 2,
            display: "grid",
            gap: { xs: 1, sm: 1.5 },
            minWidth: 0,
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
          }}
        >
          {dashStatCards.map((card) => (
            <Box key={card.label} sx={{ minWidth: 0 }}>
              <MedicineDashStatCard {...card} />
            </Box>
          ))}
        </Box>
        {noStockButHasUsage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Treatment records exist, but no formulary/lot inventory is configured yet. Usage charts are based on treatment logs; add products and receive lots to enable stock-on-hand analytics.
          </Alert>
        )}
        {uncostedUsageCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {uncostedUsageCount} usage record(s) have no cost snapshot and are excluded from spend totals.
          </Alert>
        )}
        {dashLoading && <LinearProgress sx={{ mb: 2 }} />}
        <Box
          sx={{
            display: "grid",
            gap: { xs: 1.5, sm: 2 },
            minWidth: 0,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <DashboardChartCard title="Stock on hand by product">
              <HorizontalRankBarChart
                data={stockByProduct}
                valueKey="qty"
                height={stockChartHeight}
                yAxisWidth={chartYAxisWidth}
                tooltipLabel="Qty on hand"
              />
            </DashboardChartCard>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <DashboardChartCard
              title="Treatment usage by medicine"
              caption="Total dispensed quantity per medicine (entries without qty count as 1)."
            >
              <HorizontalRankBarChart
                data={usageByMedicine}
                valueKey="qty"
                height={usageMedChartHeight}
                yAxisWidth={chartYAxisWidth}
                tooltipLabel="Quantity"
              />
            </DashboardChartCard>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <DashboardChartCard title="Usage by scope">
              <HorizontalRankBarChart
                data={usageByScope}
                valueKey="qty"
                height={usageScopeChartHeight}
                yAxisWidth={isMobile ? 68 : 88}
                colors={["#1976d2", "#00838f", "#ed6c02"]}
                tooltipLabel="Total quantity"
              />
            </DashboardChartCard>
          </Box>
          <Box sx={{ gridColumn: { xs: "1 / -1", lg: "1 / span 2" }, minWidth: 0 }}>
            <DashboardChartCard
              title={
                spendByMedicine.length > 0
                  ? "Spend by medicine (from usage)"
                  : "Stock value by product (at cost)"
              }
            >
              <HorizontalRankBarChart
                data={spendByMedicineChart}
                valueKey="total"
                height={spendChartHeight}
                yAxisWidth={chartYAxisWidth}
                colors={CHART_COLORS}
                formatValue={formatSpendAxis}
                formatTooltip={formatSpendTooltip}
                tooltipLabel={spendTooltipLabel}
              />
            </DashboardChartCard>
          </Box>
          <Box sx={{ gridColumn: { xs: "1 / -1", lg: "3 / span 1" }, minWidth: 0 }}>
            <DashboardChartCard title="Spend trend by date">
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <LineChart data={spendTrend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis
                    width={isMobile ? 52 : 68}
                    tickFormatter={formatSpendAxis}
                    tick={{ fontSize: isMobile ? 10 : 11 }}
                  />
                  <ReTooltip
                    formatter={(value) => [formatSpendTooltip(value), "Total"]}
                  />
                  <Line type="monotone" dataKey="total" stroke="#ed6c02" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </DashboardChartCard>
          </Box>
          <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <Paper sx={{ p: 2, width: "100%" }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                Recent usage and cost ledger
              </Typography>
              {isMobile ? (
                <Stack spacing={1}>
                  {dashLoading
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <MobileListCardSkeleton key={i} height={72} />
                      ))
                    : ledgerRows.length === 0
                      ? (
                        <Typography color="text.secondary">No usage records.</Typography>
                      )
                      : ledgerPagedRows.map((row) => (
                          <UsageLedgerCard key={row.id} row={row} />
                        ))}
                  {ledgerRows.length > 0 && (
                    <MobilePagination
                      page={ledgerPage}
                      pageSize={LEDGER_PAGE_SIZE}
                      rowCount={ledgerRows.length}
                      loading={dashLoading}
                      onPageChange={({ page }) => setLedgerPage(page)}
                    />
                  )}
                </Stack>
              ) : (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box sx={{ minWidth: { xs: 760, md: "100%" } }}>
                    <DataGrid
                      rows={ledgerRows}
                      getRowId={(r) => r.id}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: LEDGER_PAGE_SIZE } },
                      }}
                      columns={[
                        { field: "date", headerName: "Date", width: 120 },
                        { field: "medicine", headerName: "Medicine", flex: 1, minWidth: 140 },
                        { field: "medicine_lot_id", headerName: "Lot", width: 90 },
                        { field: "dispensed_qty", headerName: "Qty", width: 110 },
                        { field: "dispensed_unit", headerName: "Unit", width: 90 },
                        {
                          field: "dispensed_unit_cost",
                          headerName: "Unit cost",
                          width: 120,
                          valueGetter: (_, row) =>
                            row.dispensed_unit_cost == null ? "—" : formatMoney(row.dispensed_unit_cost, 4),
                        },
                        {
                          field: "dispensed_total_cost",
                          headerName: "Total cost",
                          width: 120,
                          valueGetter: (_, row) =>
                            row.dispensed_total_cost == null ? "—" : formatMoney(row.dispensed_total_cost, 2),
                        },
                        { field: "dispensed_currency", headerName: "Currency", width: 100 },
                        { field: "treatment_scope", headerName: "Scope", width: 110 },
                        {
                          field: "subject",
                          headerName: "Subject",
                          flex: 1,
                          minWidth: 150,
                          valueGetter: (_, row) =>
                            row.treatment_scope === "group"
                              ? row.group_name || "Group"
                              : row.animal_tag_number || row.animal_id || "Animal",
                        },
                      ]}
                      sx={{ border: "none" }}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button variant="contained" onClick={() => setAddProdOpen(true)} fullWidth sx={{ width: { sm: "auto" } }}>
            Add product
          </Button>
        </Stack>
        <Paper>
          {isMobile ? (
            <Stack spacing={1} sx={{ p: 2 }}>
              {prodLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <MobileListCardSkeleton key={i} />
                  ))
                : prodRows.length === 0
                  ? (
                    <Typography color="text.secondary">No products found.</Typography>
                  )
                  : prodRows.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onToggle={handleToggleProduct}
                        toggling={togglingId === product.id}
                      />
                    ))}
              <MobilePagination
                page={prodPage.page}
                pageSize={prodPage.pageSize}
                rowCount={prodCount}
                loading={prodLoading}
                onPageChange={setProdPage}
              />
            </Stack>
          ) : (
            <DataGrid
              rows={prodRows}
              columns={prodColumns}
              getRowId={(r) => r.id}
              loading={prodLoading}
              pagination
              paginationMode="server"
              rowCount={prodCount}
              paginationModel={prodPage}
              onPaginationModelChange={setProdPage}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              getRowClassName={(params) =>
                params.row.is_active ? "product-active" : "product-inactive"
              }
              sx={{
                border: "none",
                "& .MuiDataGrid-row.product-active": {
                  borderLeft: "3px solid #2e7d32",
                },
                "& .MuiDataGrid-row.product-inactive": {
                  bgcolor: "#fafafa",
                  "&:hover": { bgcolor: "#f0f0f0" },
                },
              }}
            />
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button variant="contained" onClick={openLotDialog} fullWidth sx={{ width: { sm: "auto" } }}>
            Receive stock
          </Button>
        </Stack>
        <Paper>
          {isMobile ? (
            <Stack spacing={1} sx={{ p: 2 }}>
              {lotLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <MobileListCardSkeleton key={i} height={108} />
                  ))
                : lotRows.length === 0
                  ? (
                    <Typography color="text.secondary">No stock lots found.</Typography>
                  )
                  : lotRows.map((row) => (
                      <LotCard
                        key={row.id}
                        row={row}
                        productName={
                          lotProductNameMap.get(row.product_id) ||
                          `Product ${row.product_id}`
                        }
                        onEdit={openEditLotDialog}
                      />
                    ))}
              <MobilePagination
                page={lotPage.page}
                pageSize={lotPage.pageSize}
                rowCount={lotCount}
                loading={lotLoading}
                onPageChange={setLotPage}
              />
            </Stack>
          ) : (
            <DataGrid
              rows={lotRows}
              columns={lotColumns}
              getRowId={(r) => r.id}
              loading={lotLoading}
              pagination
              paginationMode="server"
              rowCount={lotCount}
              paginationModel={lotPage}
              onPaginationModelChange={setLotPage}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              getRowClassName={(params) => `lot-${getLotStatus(params.row)}`}
              sx={{
                border: "none",
                "& .MuiDataGrid-row.lot-inStock": {
                  borderLeft: "3px solid #2e7d32",
                },
                "& .MuiDataGrid-row.lot-expiring": {
                  bgcolor: "#fff8e1",
                  "&:hover": { bgcolor: "#fff3cd" },
                },
                "& .MuiDataGrid-row.lot-expired": {
                  bgcolor: "#ffebee",
                  "&:hover": { bgcolor: "#ffcdd2" },
                },
                "& .MuiDataGrid-row.lot-depleted": {
                  bgcolor: "#fafafa",
                  opacity: 0.92,
                  "&:hover": { bgcolor: "#f0f0f0" },
                },
              }}
            />
          )}
        </Paper>
      </TabPanel>

      <Dialog
        open={addProdOpen}
        onClose={() => setAddProdOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add medicine product</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={newProd.name}
            onChange={(e) => setNewProd((s) => ({ ...s, name: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Brand"
            value={newProd.brand}
            onChange={(e) => setNewProd((s) => ({ ...s, brand: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Default unit"
            value={newProd.default_unit}
            onChange={(e) =>
              setNewProd((s) => ({ ...s, default_unit: e.target.value }))
            }
            helperText="e.g. ml, dose, tab"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitNewProduct}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lotOpen}
        onClose={() => setLotOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receive stock lot</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1, mb: 2 }}
            options={prodOptions}
            getOptionLabel={(o) => o?.name ?? ""}
            value={lotForm.product}
            onChange={(_, v) =>
              setLotForm((s) => ({
                ...s,
                product: v,
                unit: v?.default_unit || s.unit,
              }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Product" required />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
          <TextField
            fullWidth
            label="Quantity received"
            value={lotForm.quantity_remaining}
            onChange={(e) =>
              setLotForm((s) => ({ ...s, quantity_remaining: e.target.value }))
            }
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Unit"
            value={lotForm.unit}
            onChange={(e) =>
              setLotForm((s) => ({ ...s, unit: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mb: 2 }}>
            <TextField
              fullWidth
              label="Unit cost"
              type="number"
              inputProps={{ min: 0, step: "0.0001" }}
              value={lotForm.unit_cost}
              onChange={(e) =>
                setLotForm((s) => ({ ...s, unit_cost: e.target.value }))
              }
              helperText="Cost per unit received (e.g., per ml/tablet)."
            />
            <TextField
              fullWidth
              label="Currency"
              value={lotForm.currency}
              onChange={(e) =>
                setLotForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))
              }
              inputProps={{ maxLength: 8 }}
            />
          </Box>
          {lotLineTotalPreview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Estimated line total: {lotForm.currency || "UGX"} {formatMoney(lotLineTotalPreview, 2)}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Batch number"
            value={lotForm.batch_number}
            onChange={(e) =>
              setLotForm((s) => ({ ...s, batch_number: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Expiry date"
            type="date"
            value={lotForm.expiry_date}
            onChange={(e) =>
              setLotForm((s) => ({ ...s, expiry_date: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Supplier"
            value={lotForm.supplier}
            onChange={(e) =>
              setLotForm((s) => ({ ...s, supplier: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLotOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitLot}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editLotOpen}
        onClose={() => setEditLotOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit stock lot</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Quantity remaining"
            value={editLotForm.quantity_remaining}
            onChange={(e) =>
              setEditLotForm((s) => ({ ...s, quantity_remaining: e.target.value }))
            }
            sx={{ mt: 1, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Unit"
            value={editLotForm.unit}
            onChange={(e) =>
              setEditLotForm((s) => ({ ...s, unit: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mb: 2 }}>
            <TextField
              fullWidth
              label="Unit cost"
              type="number"
              inputProps={{ min: 0, step: "0.0001" }}
              value={editLotForm.unit_cost}
              onChange={(e) =>
                setEditLotForm((s) => ({ ...s, unit_cost: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="Currency"
              value={editLotForm.currency}
              onChange={(e) =>
                setEditLotForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))
              }
              inputProps={{ maxLength: 8 }}
            />
          </Box>
          <TextField
            fullWidth
            label="Batch number"
            value={editLotForm.batch_number}
            onChange={(e) =>
              setEditLotForm((s) => ({ ...s, batch_number: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Expiry date"
            type="date"
            value={editLotForm.expiry_date}
            onChange={(e) =>
              setEditLotForm((s) => ({ ...s, expiry_date: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Supplier"
            value={editLotForm.supplier}
            onChange={(e) =>
              setEditLotForm((s) => ({ ...s, supplier: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLotOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEditLot}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
